/**
 * @citeclerk/plan/promptPricing — THE ONE RENDERER for the "Plans and pricing" block that Clark
 * speaks out loud.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🔴 THE DEFECT THIS EXISTS TO MAKE IMPOSSIBLE. RUN H, 2026-08-12.
 *
 * There were TWO Clark system prompts, both live, and only one had been updated:
 *
 *   apps/main/public/clark.js                            (help page)  — five current tiers
 *   apps/main/client/src/screens/Clark/systemPrompt.js   (in-app)     — a RETIRED FOUR-TIER LIST
 *
 * The in-app one — the one baked into the shipped bundle — told users Attorney was **$15/mo when
 * it is $30**, Free got 10 citations when it gets 30, and offered **`Partner` and `Firm`**, tiers
 * `apps/main/server/lib/plan.js` says outright were removed. A user who asked in-app Clark what
 * CiteClerk costs was quoted half the real price for a plan and sold one that cannot be bought.
 *
 * Two files that must agree and don't is the same failure `@citeclerk/plan` was created to end for
 * the limit tables — and it recurred one layer up, in prose, where no table parser was looking.
 * The remedy is the same remedy: ONE renderer, no literals at the call sites, and a guard.
 *
 * 🔴 NOTHING IN THIS FILE IS A NUMBER. Every price, allowance and pack size below is read out of
 * limits.cjs at render time. If a digit ever appears in the template text, this module has
 * reacquired the exact defect it was built to remove — and promptPricing.test.mjs fails on it.
 *
 * ⚠️ AND THAT SENTENCE IS THE TRAP, NOT THE GUARANTEE — RUN GQ1 / RUN HA, 2026-08-22.
 * The defect that survived every guard in this file was the part that is NOT a number: the VERB.
 * "Packs are buyable on EVERY plan including Free" was hand-written prose in a block certified
 * byte-for-byte, and it made a MONEY claim the product could not honour. Deriving the digits
 * guarantees the digits. A byte-lock proves the block has not DRIFTED; it cannot prove the block
 * was TRUE when it was written. Every sentence added below must be checkable against something,
 * and if it is not, it does not go in — see the à-la-carte paragraph for what that looks like.
 *
 * WHO USES IT
 *   · apps/main/client/src/screens/Clark/systemPrompt.js — imports and interpolates. DERIVED.
 *   · apps/main/public/clark.js — a plain <script> served statically with NO build step, so it
 *     cannot import. It carries the rendered block VERBATIM, and clarkPromptPricing.test.mjs
 *     re-renders from canonical and byte-compares, naming the prompt and the field on a mismatch.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * 🔴 WHAT IS DELIBERATELY NOT RENDERED, AND WHY. An omission here is a DECISION, not a gap.
 *
 *   matters — canonical has no per-tier matters allowance. `MATTER_CAP` is a FLAT ceiling of 999
 *             for every account including Free; it is not "how many matters your plan includes".
 *             The live pricing page sells "2 matters · 1 draft each" and apps/steel's table carries
 *             matters 0/0/2/2/5. Three different ideas. Rendering any of them from here would be
 *             inventing a canonical number that does not exist, so Clark says nothing about matter
 *             counts and falls through to its "I'm not certain — ask support@" rule.
 *
 *   history — retention is not in the launch table at all. apps/main/server/lib/plan.js carries a
 *             `history` column and its own header says it is "NOT in the locked pricing table" and
 *             a carry-over pending a ruling. Not canonical, so not spoken.
 *
 *   drafts  — IS rendered, from canonical, as a MONTHLY pool (the founder's launch table row:
 *             0/0/1/2/3). Be aware that apps/steel's `draftsPerMatter` is a PER-MATTER ceiling
 *             (0/0/1/1/1) and apps/seafoam's tierUnits.js calls it a product invariant fixed at 1.
 *             Three denominators, and tableParity.test.mjs refuses to compare them for that reason.
 *             What Clark says is the founder's number; what Steel enforces is a different shape.
 *             That conflict is REPORTED, not resolved here — see ops/run-h/RUN_H_REPORT.md §5.
 */

const {
  TIERS, TIER_LABEL, PLAN_LIMITS, PRICING, PACKS, METER_LABEL, conditionalFor,
} = require('./limits.cjs');

// ── formatting ───────────────────────────────────────────────────────────────
/** US cents → "$9" / "$9.50". Whole dollars lose the ".00" because a price list reads better. */
function usd(cents) {
  const n = Number(cents) || 0;
  return n % 100 === 0 ? `$${n / 100}` : `$${(n / 100).toFixed(2)}`;
}

/** 1000 → "1,000". Allowances above a thousand read as prices otherwise. */
function n(v) {
  return Number(v).toLocaleString('en-US');
}

/** "1 citation" / "30 citations". A pluraliser, because "1 Doc Checks" is how copy rot starts. */
function plural(count, one, many) {
  return `${n(count)} ${Number(count) === 1 ? one : many}`;
}

// ── the price line ───────────────────────────────────────────────────────────
/**
 * `PRICING[tier].annual` is the ANNUAL-BILLED-MONTHLY rate in cents — what the customer sees per
 * month on an annual commitment. The yearly total a price list quotes is that × 12; deriving it
 * rather than storing it is why "$180/yr" beside "$15/mo" could not happen here.
 */
function priceLabel(tier) {
  const p = PRICING[tier];
  if (!p || (!p.monthly && !p.annual)) return usd(0);
  return `${usd(p.monthly)}/mo (${usd(p.annual * 12)}/yr)`;
}

/** The largest annual discount across the paid tiers, as a whole percent. Derived, never claimed. */
function maxAnnualSavingPct() {
  let best = 0;
  for (const t of TIERS) {
    const p = PRICING[t];
    if (!p || !p.monthly) continue;
    best = Math.max(best, 1 - p.annual / p.monthly);
  }
  return Math.round(best * 100);
}

// ── the allowance line ───────────────────────────────────────────────────────
/**
 * The four canonical meters, in the order a customer cares about them. A meter whose limit is 0 is
 * OMITTED rather than printed as "0" — in this table 0 means "not included on this tier", and a
 * bullet reading "0 Doc Checks/month" invites the reader to think it is a countdown they can top up.
 * What the tier does NOT include is stated once, plainly, by the caller's surrounding copy.
 */
function allowances(tier) {
  const L = PLAN_LIMITS[tier];
  const parts = [];
  if (L.citations) parts.push(`${plural(L.citations, 'citation', 'citations')}/month`);
  // "research queries", not "searches" — 2026-08-15. `queries` is the canonical meter name and the
  // pool covers Clark AND CiteClerk Research combined, which "searches" does not say; a user told
  // they have "10 searches" does not know that asking Clark spends one. The pricing page moved to
  // the same noun in the same run, so the two surfaces say the same word for the same pool.
  if (L.queries) parts.push(`${plural(L.queries, 'research query', 'research queries')}/month`);
  if (L.docChecks) parts.push(`${plural(L.docChecks, 'Doc Check doc', 'Doc Check docs')}/month`);
  if (L.drafts) parts.push(`${plural(L.drafts, 'draft', 'drafts')}/month`);
  return parts.join(', ');
}

/**
 * ── 🔴 RUN XD — THE CONDITIONAL ALLOWANCE, SAID OUT LOUD FOR THE SAME REASON THE ZEROES ARE ────
 *
 * ADAM'S RULING 2026-08-28: Free grants 10 citations, and 20 with a verified .edu.
 *
 * `allowances()` above states only the BASE, which is right for a price list and WRONG as the only
 * signal here — this prompt is Clark's ENTIRE knowledge of what the product costs, and a customer
 * who asks "what do I get free?" would be told 10 and nothing else. That answer is not false, but
 * it is the half that loses the company a student, and Clark has no other source to correct it
 * from. It is the identical argument the block below makes for the zeroes: silence in a system
 * prompt is not neutral, because a model fills it.
 *
 * DERIVED, never typed: the number and the condition both come from CONDITIONAL_LIMITS.
 */
function conditionalAllowances(tier) {
  const parts = [];
  for (const [meter, noun] of [['citations', 'citation'], ['queries', 'research query'],
                               ['docChecks', 'Doc Check doc'], ['drafts', 'draft']]) {
    const cell = conditionalFor(tier, meter);
    if (!cell) continue;
    for (const [cond, value] of Object.entries(cell)) {
      if (cond !== 'eduVerified') continue;   // the only condition the vocabulary has a sentence for
      parts.push(`${plural(value, noun, `${noun}s`)}/month with a verified .edu email`);
    }
  }
  return parts.join(', ');
}

/** Per-tier prose that is NOT a number: who the tier is for, and any gate on buying it. */
const TIER_NOTE = Object.freeze({
  free: '',
  student: 'Verified .edu email required.',
  student_ultra: 'Verified .edu email required.',
  attorney: 'Most popular.',
  attorney_ultra: '',
});

/**
 * 🔴 THE ZEROES, SAID OUT LOUD. `allowances()` omits a meter whose limit is 0, which is right for a
 * price list and WRONG as the only signal — a tier line that simply does not mention Doc Checks
 * lets a model fill the silence with the tier above. So every 0 is stated explicitly, derived from
 * the same table, and phrased as exclusion ("does not include") rather than as a quantity.
 */
function exclusions() {
  // Meters excluded from the SAME set of tiers are merged into one sentence. Three near-identical
  // sentences in a row read as boilerplate and get skimmed — by a human and by a model.
  const groups = new Map();
  for (const [meter, label] of [['queries', 'research queries'], ['docChecks', 'Doc Checks'], ['drafts', 'drafts']]) {
    const zero = TIERS.filter((t) => PLAN_LIMITS[t][meter] === 0);
    if (!zero.length) continue;
    const first = TIERS.find((t) => PLAN_LIMITS[t][meter] > 0) || null;
    const key = `${zero.join('|')}>${first}`;
    if (!groups.has(key)) groups.set(key, { zero, first, labels: [] });
    groups.get(key).labels.push(label);
  }
  const list = (xs) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} or ${xs[xs.length - 1]}`);
  return [...groups.values()].map((g) =>
    `${g.zero.map((t) => TIER_LABEL[t]).join(' and ')} do NOT include ${list(g.labels)}`
    + (g.first ? ` — those start on ${TIER_LABEL[g.first]}` : ''));
}

// ── the block ────────────────────────────────────────────────────────────────
/**
 * Render the whole "## Plans and pricing" section, exactly as both Clark prompts must carry it.
 *
 * 🔴 THE RETIRED-TIER SENTENCE IS NOT DECORATION. The defect that opened RUN H was Clark OFFERING
 * a plan that cannot be bought. Naming the retired tiers explicitly is the only instruction that
 * survives a model's pretraining memory of an older price list; "here are the five plans" does not
 * stop a model that has seen "Partner" a thousand times from mentioning it.
 */
function renderPlansSection() {
  const lines = ['## Plans and pricing', ''];

  for (const tier of TIERS) {
    const note = TIER_NOTE[tier] ? `${TIER_NOTE[tier]} ` : '';
    const allow = allowances(tier);
    // RUN XD — the conditional rides the SAME line as the base, as a second clause, so a model
    // reading the tier cannot pick up one number without the other.
    const cond = conditionalAllowances(tier);
    const tail = cond ? `${allow}, or ${cond}` : allow;
    lines.push(`**${TIER_LABEL[tier]} — ${priceLabel(tier)}** · ${note}${tail}.`);
    lines.push('');
  }

  lines.push(
    `All 7 source types, Advanced Options and reporter preference are on every plan, including `
    + `${TIER_LABEL.free}. ${exclusions().join('. ')}.`,
    '');

  lines.push(
    `There are exactly ${TIERS.length} plans and they are the ${TIERS.length} above. `
    + '**There is no Partner plan and no Firm plan** — those tiers were retired and cannot be '
    + 'bought. Never offer them, never price them, and never describe a feature as belonging to '
    + 'them. If a user asks about Partner or Firm, say they are no longer offered and point to the '
    + 'current lineup.',
    '');

  // 🔴 THE PACK PHRASE IS BUILT, NOT READ OFF `PACKS[k].label`. The stored label is the internal
  // one ("10 queries"), and the customer-facing noun for that meter is "research queries" — the
  // word the pricing page and the tier lines above both use since 2026-08-15. Reading the stored
  // label here would have Clark quote a different name for the same pack than the page a user is
  // looking at while they ask. The UNITS and the PRICE still come from PACKS; only the noun is copy.
  const PACK_NOUN = {
    citations: (n) => plural(n, 'citation', 'citations'),
    queries: (n) => plural(n, 'research query', 'research queries'),
    docChecks: (n) => plural(n, 'Doc Check document', 'Doc Check documents'),
    drafts: (n) => plural(n, 'draft', 'drafts'),
  };
  const packLine = ['citations', 'queries', 'docChecks', 'drafts']
    .map((k) => `${PACK_NOUN[k](PACKS[k].units)} — ${usd(PACKS[k].priceCents)}`)
    .join(' · ');
  // 🔴 RUN HA — WHAT THIS PARAGRAPH NO LONGER CLAIMS, AND WHY IT CLAIMS NOTHING INSTEAD.
  //
  // It used to open "Packs are buyable on EVERY plan including Free" and go on to promise that a
  // Free user who bought a query pack could run queries. That is a MONEY claim, it sat inside the
  // block whose header boasts "NOTHING IN THIS FILE IS A NUMBER", and it survived every guard for
  // exactly that reason: the derivation guaranteed the DIGITS and left the VERB hand-written. A
  // gate that byte-compares a block certifies it has not drifted. It says nothing about whether
  // the block was true when it was written.
  //
  // ⚠️ AND THE OBVIOUS CORRECTION — "packs are not on sale" — IS ALSO UNSUBSTANTIATED. Purchasing
  // is gated on `ALACARTE_LIVE` (apps/steel/server/lib/alacarte.js), whose code default is OFF and
  // whose refusal `403 alacarte_disabled` three test suites treat as load-bearing — but which is a
  // live fly SECRET on the research app, recorded in the same digest group as CLARK_RETRIEVAL and
  // METERING_ENFORCED, both known to be "1" (apps/steel/ops/bundle-h2/probe/prod_env.json). Equal
  // digests mean equal values. So the flag may well be ON in production and this module cannot
  // read it: the rendered block is byte-compared against a static file with no build step, so a
  // render that varied with an env var would certify different bytes on different machines.
  //
  // ⭐ SO THE BLOCK STATES ONLY WHAT IS TRUE UNDER BOTH BRANCHES OF THE FLAG. What a pack contains
  // and what it costs are canonical and are rendered. Whether one can be bought today is not
  // knowable here, so Clark asserts NEITHER and routes the question — which is precisely the move
  // this module already documents for `matters` at the top of the file: an omission here is a
  // DECISION, not a gap.
  lines.push(
    `**À la carte** · Packs are listed at: ${packLine}. Each pack, once bought, expires one year `
    + 'from that day on its own clock. **Do NOT tell a user a pack can be bought, or that buying '
    + 'one will let them past their plan allowance** — à-la-carte purchasing is gated and may not '
    + 'be switched on. If a user asks whether they can buy a pack or how to, say you are not '
    + 'certain it is available yet and send them to billing@citeclerk.com. The path you CAN state '
    + 'plainly is a plan change.',
    '');

  lines.push(
    // NOT stated here: how a plan change is initiated (self-serve vs email). That is a product
    // wiring fact, not a canonical limit, and the two prompts disagree about it today — see the
    // report. What IS canonical is the carryover rule, which is remainingFor()'s documented
    // behaviour: `new_tier_limit − usage_since_period_start`, floored at zero.
    `Annual billing saves up to ${maxAnnualSavingPct()}%. An upgrade raises the ceiling but does `
    + 'NOT reset the counter — usage already spent this period still counts against the new, '
    + 'larger allowance, and a downgrade never makes the remaining balance negative.',
    '');

  lines.push(
    `**${METER_LABEL.docChecks} and citations are separate pools.** Generating a citation and `
    + 'checking one both draw on the citations allowance; a Doc Check draws on its own. Running out '
    + 'of one does not affect the other.');

  return lines.join('\n');
}

/** The rendered block, computed once. This is the string both prompts must carry. */
const PLANS_AND_PRICING_MD = renderPlansSection();

module.exports = { renderPlansSection, PLANS_AND_PRICING_MD, usd, maxAnnualSavingPct };
